import React, { Fragment, useCallback, useMemo } from 'react';
import { extent, max, bisector } from 'd3-array';
import { curveCatmullRom } from '@visx/curve';
import { Group } from '@visx/group';
import { GridRows } from '@visx/grid';
import { Bar, Line, LinePath } from '@visx/shape';
import { scaleTime, scaleLinear } from '@visx/scale';
import { DateValue } from '@visx/mock-data/lib/generators/genDateValue';
import { withTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip';
import { localPoint } from '@visx/event';

type TooltipData = DateValue;

export interface ChartLine {
  data: DateValue[];
  label: string;
  color: string;
}

export interface ChartLineData {
  [lineName: string]: ChartLine;
}

export type ChartLineKeys = keyof ChartLineData;

export type ChartProps = {
  lineData: ChartLineData;
  width: number;
  height: number;
  activeLine: keyof ChartLineData;
};

export const getCountDateValues = (counts: Record<string, number>) =>
  Object.entries(counts).map(
    ([date, value]) =>
      ({
        date: new Date(date),
        value,
      } as DateValue),
  );

export const getLineTotalCount = (counts?: ChartLine): number =>
  counts ? counts.data.reduce((acc, data) => acc + data.value, 0) : 0;

function Chart({
  lineData,
  width,
  height,
  showTooltip,
  hideTooltip,
  tooltipData,
  tooltipTop = 0,
  tooltipLeft = 0,
  activeLine,
}: ChartProps & WithTooltipProvidedProps<TooltipData>) {
  // data accessors
  const getX = (d: DateValue) => d.date;
  const getY = (d: DateValue) => d.value;
  const bisectDate = bisector<DateValue, Date>((d) => new Date(d.date)).left;

  // data
  const allData = Object.values(lineData).reduce(
    (rec, value) => rec.concat(value.data),
    [] as DateValue[],
  );

  // scales
  const xScale = scaleTime<number>({ domain: extent(allData, getX) as [Date, Date] });
  const yMax = max(allData, getY) as number;
  const yGridScale = scaleLinear<number>({ domain: [0, yMax] });
  xScale.range([0, width]);
  yGridScale.range([height, 0]);
  // all chart lines will be plotted relative to an individual line's heighest value
  // so the y-axis is inconsistent for each line plotted
  const yScales = useMemo<{ [lineDateKey in keyof typeof lineData]: any }>(
    () =>
      Object.keys(lineData).reduce((scales, key) => {
        const yScaleMax = max(lineData[key].data, getY) as number;
        const yScale = scaleLinear<number>({ domain: [0, yScaleMax || 1] });
        yScale.range([height, 0]);
        // eslint-disable-next-line no-param-reassign
        scales[key] = yScale;
        return scales;
      }, {} as { [lineDateKey in keyof typeof lineData]: any }),
    [lineData, height],
  );

  // state
  const defaultLineOrder: Array<keyof ChartLineData> = Object.keys(lineData);
  const defaultActiveLineIndex = defaultLineOrder.indexOf(activeLine);
  const orderedLines: Array<keyof ChartLineData> = [
    ...defaultLineOrder.slice(0, defaultActiveLineIndex),
    ...defaultLineOrder.slice(defaultActiveLineIndex + 1),
    activeLine,
  ];

  // handlers
  const handleTooltip = useCallback(
    (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
      const { x } = localPoint(event) || { x: 0 };
      const x0 = xScale.invert(x);
      const currentChartLineData = lineData[activeLine]?.data;
      if (!currentChartLineData) return;
      const index = bisectDate(currentChartLineData, x0, 1);
      const d0 = currentChartLineData[index - 1];
      const d1 = currentChartLineData[index];
      let d = d0;
      if (d1 && getX(d1)) {
        d = x0.valueOf() - getX(d0).valueOf() > getX(d1).valueOf() - x0.valueOf() ? d1 : d0;
      }

      showTooltip({
        tooltipData: d,
        tooltipLeft: x,
        tooltipTop: yScales[activeLine](getY(d)),
      });
    },
    [showTooltip, xScale, yScales, activeLine, bisectDate, lineData],
  );

  const xSpacing = xScale(getX(lineData[defaultLineOrder[0]].data[1]));
  const xSectionNum = Math.round(tooltipLeft / xSpacing);

  return (
    <div>
      <svg width={width} height={height}>
        <GridRows
          top={0}
          scale={yGridScale}
          width={width}
          stroke="rgb(59, 130, 246)"
          strokeDasharray="1,3,0"
          strokeOpacity={1}
          tickValues={[yMax / 5, (yMax * 2) / 5, (yMax * 3) / 5, (yMax * 4) / 5, yMax - 3]}
          pointerEvents="none"
        />

        {orderedLines.map((key) => {
          if (lineData[key] === undefined) return <Fragment key={key} />;

          const { data, color } = lineData[key] as ChartLine;
          const yScale = yScales[key];

          return (
            <Group key={key} top={0} left={0}>
              <LinePath<DateValue>
                curve={curveCatmullRom}
                data={data}
                x={(d) => xScale(getX(d)) ?? 0}
                y={(d) => yScale(getY(d)) ?? 0}
                stroke={color}
                strokeWidth={4}
                strokeOpacity={key === activeLine || tooltipLeft === 0 ? 1 : 0.4}
                shapeRendering="geometricPrecision"
              />
              {data.map((d, j) => {
                if (tooltipLeft === 0 || xSectionNum !== j) return null;
                if (activeLine !== key) return null;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Fragment key={`${key}${j}`}>
                    <Line
                      from={{ x: xScale(getX(d)), y: tooltipTop }}
                      to={{ x: xScale(getX(d)), y: height }}
                      stroke="white"
                      strokeWidth={1}
                      pointerEvents="none"
                      strokeDasharray="5,2"
                    />
                    <circle
                      r={10}
                      cx={xScale(getX(d))}
                      cy={yScale(getY(d))}
                      fill="black"
                      stroke="white"
                      strokeWidth={4}
                      pointerEvents="none"
                    />
                  </Fragment>
                );
              })}
            </Group>
          );
        })}

        <Bar
          width={width}
          height={height}
          fill="transparent"
          onTouchStart={handleTooltip}
          onTouchMove={handleTooltip}
          onMouseMove={handleTooltip}
          onMouseLeave={() => hideTooltip()}
        />
      </svg>
      {tooltipData && (
        <div>
          <TooltipWithBounds
            top={tooltipTop - 60}
            left={tooltipLeft + 12}
            style={{
              ...defaultStyles,
              background: '#111',
              borderRadius: '1rem',
              padding: '1rem',
              color: 'white',
              zIndex: 1,
            }}
          >
            <h3 className="text-sm" style={{ color: lineData[activeLine]?.color || 'white' }}>
              {lineData[activeLine].label}
            </h3>
            <p className="my-.5 text-lg font-bold">{getY(tooltipData).toLocaleString()}</p>
            <p className="text-sm">{`on ${getX(tooltipData).toLocaleDateString()}`}</p>
          </TooltipWithBounds>
        </div>
      )}
    </div>
  );
}

export default withTooltip<ChartProps, TooltipData>(Chart);
